import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Grid,
  Card,
  Badge,
  ActionIcon,
  Modal,
  NumberInput,
  Select,
  rem,
  Box,
  Table,
  ScrollArea,
  Textarea,
  Combobox,
  InputBase,
  useCombobox
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconCoin,
  IconShoppingCart,
  IconCheck,
  IconX,
  IconFileText
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { collection, query, where, onSnapshot, doc, setDoc, deleteDoc, getDocs } from 'firebase/firestore';
import firestore from '../../utils/firestore';
import calendar from '../../utils/lunar';
import festival from '../../utils/festival';
import format_with_substring from '../../utils/number';

const ElderlyKeyinIndex = () => {
  const [showDate, setShowDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [editRecord, setEditRecord] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [batchOpened, { open: openBatch, close: closeBatch }] = useDisclosure(false);
  const [nowFestival, setNowFestival] = useState([]);
  const [festivalData, setFestivalData] = useState([]);

  const year = showDate.year();
  const month = showDate.month();
  const endDay = parseInt(dayjs().year(year).month(month).endOf('month').format('D'));

  // 獲取當月資料
  useEffect(() => {
    const q = query(
      collection(firestore, 'accounting'),
      where('year', '==', year),
      where('month', '==', month)
    );
    const unsubscribe = onSnapshot(q, (accounting) => {
      if (accounting.docs.length > 0) {
        const records = [];
        accounting.forEach(doc => {
          records.push({ id: doc.id, ...doc.data() });
        });
        setData(records);
      } else {
        setData([]);
      }
    });
    return () => unsubscribe();
  }, [showDate, month, year]);

  // 獲取特殊節日数據
  useEffect(() => {
    (async () => {
      try {
        const data = [];
        for (let i = 0; i < nowFestival.length; i++) {
          const element = nowFestival[i];
          const out = {
            festival: element
          };
          const q = query(
            collection(firestore, 'accounting'),
            where('lunar', '==', element)
          );
          const accounting = await getDocs(q);
          if (accounting.docs.length > 0) {
            accounting.forEach(doc => {
              const subData = doc.data();
              const income = subData.income ?? 0;
              const specialIncome = subData.specialIncome ?? 0;
              const total = income + specialIncome;
              out[subData.year] = total;
            });
          }
          data.push(out);
        }
        setFestivalData(data);
      } catch (error) {
        notifications.show({
          title: '數據載入失敗',
          message: '無法載入節日數據，請重新整理頁面',
          color: 'red'
        });
        setFestivalData([]);
      }
    })();
  }, [nowFestival]);

  // 生成每日資料
  const getDailyData = () => {
    const dailyData = [];
    for (let i = 1; i <= endDay; i++) {
      const todayLunar = calendar.solar2lunar(year, month + 1, i);
      const week = new Date(year, month, i).getDay();
      const weekNames = ['日', '一', '二', '三', '四', '五', '六'];
      
      let lunarStr = `${todayLunar.lMonth}/${todayLunar.lDay}`;
      if (festival[lunarStr]) {
        lunarStr = festival[lunarStr];
      } else if (
        todayLunar.lDay !== 1 &&
        todayLunar.lDay !== 15 &&
        todayLunar.lDay !== 2 &&
        todayLunar.lDay !== 16
      ) {
        lunarStr = '';
      }

      const dbData = data.find(j => j.day === i);
      dailyData.push({
        day: i,
        lunar: lunarStr,
        week: week,
        weekName: weekNames[week],
        ...dbData
      });
    }
    return dailyData;
  };

  // 新增或編輯記錄
  const openEditModal = (dayData = null) => {
    if (dayData && dayData.id) {
      setEditRecord({
        id: dayData.id,
        day: dayData.day,
        income: dayData.income || 0,
        specialIncome: dayData.specialIncome || 0,
        specialIncomeFrom: dayData.specialIncomeFrom || '',
        vendor: dayData.vendor || '',
        expenses: dayData.expenses || 0,
        lunar: dayData.lunar || '',
        memo: dayData.memo || ''
      });
    } else {
      setEditRecord({
        day: dayData?.day || 1,
        income: 0,
        specialIncome: 0,
        specialIncomeFrom: '',
        vendor: '',
        expenses: 0,
        lunar: dayData?.lunar || '',
        memo: ''
      });
    }
    open();
  };

  // 儲存記錄
  const saveRecord = async (record) => {
    try {
      const { id, ...saveData } = record;
      saveData.year = year;
      saveData.month = month;
      saveData.vendor = saveData.vendor || '';
      
      let docRef;
      if (id) {
        docRef = doc(firestore, 'accounting', id);
      } else {
        docRef = doc(collection(firestore, 'accounting'));
      }
      
      await setDoc(docRef, saveData, { merge: true });
      notifications.show({
        title: '儲存成功',
        message: '記帳資料已儲存',
        color: 'green',
      });
      close();
    } catch (error) {
      notifications.show({
        title: '儲存失敗',
        message: error.message,
        color: 'red',
      });
    }
  };

  // 儲存記錄並繼續下一天
  const saveRecordAndNext = async (record) => {
    try {
      const { id, ...saveData } = record;
      saveData.year = year;
      saveData.month = month;
      saveData.vendor = saveData.vendor || '';
      
      let docRef;
      if (id) {
        docRef = doc(firestore, 'accounting', id);
      } else {
        docRef = doc(collection(firestore, 'accounting'));
      }
      
      await setDoc(docRef, saveData, { merge: true });
      
      // 檢查是否為當月最後一天
      const currentDay = record.day;
      const lastDay = showDate.endOf('month').date();
      
      if (currentDay >= lastDay) {
        // 已經是最後一天，關閉模態框
        notifications.show({
          title: '儲存成功',
          message: '已完成當月最後一天記錄',
          color: 'green',
        });
        close();
      } else {
        // 繼續下一天
        notifications.show({
          title: '儲存成功',
          message: `第${currentDay}天已儲存，繼續編輯第${currentDay + 1}天`,
          color: 'green',
        });
        
        // 找到下一天的資料
        const nextDay = currentDay + 1;
        const nextDayData = getDailyData().find(d => d.day === nextDay);
        
        // 清除舊資料，載入新的一天資料
        if (nextDayData && nextDayData.id) {
          // 有現有記錄，載入完整資料
          setEditRecord({
            id: nextDayData.id,
            day: nextDay,
            income: nextDayData.income || 0,
            specialIncome: nextDayData.specialIncome || 0,
            specialIncomeFrom: nextDayData.specialIncomeFrom || '',
            vendor: nextDayData.vendor || '',
            expenses: nextDayData.expenses || 0,
            lunar: nextDayData.lunar || '',
            memo: nextDayData.memo || ''
          });
        } else {
          // 沒有現有記錄，創建空白記錄
          setEditRecord({
            day: nextDay,
            income: 0,
            specialIncome: 0,
            specialIncomeFrom: '',
            vendor: '',
            expenses: 0,
            lunar: nextDayData?.lunar || '',
            memo: ''
          });
        }
      }
    } catch (error) {
      notifications.show({
        title: '儲存失敗',
        message: error.message,
        color: 'red',
      });
    }
  };

  // 刪除記錄
  const deleteRecord = async (id) => {
    try {
      await deleteDoc(doc(firestore, 'accounting', id));
      notifications.show({
        title: '刪除成功',
        message: '記帳資料已刪除',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: '刪除失敗',
        message: error.message,
        color: 'red',
      });
    }
  };

  // 批量儲存記錄
  const saveBatchRecords = async (batchData) => {
    try {
      const savePromises = batchData.map(async (record) => {
        const { id, ...saveData } = record;
        saveData.year = year;
        saveData.month = month;
        saveData.vendor = saveData.vendor || '';
        
        let docRef;
        if (id) {
          docRef = doc(firestore, 'accounting', id);
        } else {
          docRef = doc(collection(firestore, 'accounting'));
        }
        
        return setDoc(docRef, saveData, { merge: true });
      });

      await Promise.all(savePromises);
      notifications.show({
        title: '批量儲存成功',
        message: `已成功儲存 ${batchData.length} 筆記錄`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: '批量儲存失敗',
        message: error.message,
        color: 'red',
      });
    }
  };

  const dailyData = getDailyData();

  // 處理節日資料
  const nowFestivalT = [];
  for (let i = 1; i <= endDay; i++) {
    const todayLunar = calendar.solar2lunar(year, month + 1, i);
    let lunarStr = `${todayLunar.lMonth}/${todayLunar.lDay}`;
    if (festival[lunarStr]) {
      lunarStr = festival[lunarStr];
    } else if (
      todayLunar.lDay !== 1 &&
      todayLunar.lDay !== 15 &&
      todayLunar.lDay !== 2 &&
      todayLunar.lDay !== 16
    ) {
      lunarStr = '';
    }
    
    if (lunarStr !== '' && nowFestivalT.indexOf(lunarStr) === -1) {
      nowFestivalT.push(lunarStr);
    }
  }
  
  if (JSON.stringify(nowFestivalT) !== JSON.stringify(nowFestival)) {
    setNowFestival(nowFestivalT);
  }

  return (
    <Container size="xl" p={0}>
      <Stack gap={{ base: 'md', sm: 'xl' }}>
        {/* 標題與月份選擇 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }} bg="blue.0">
          <Stack align="center" gap="md">
            <Title order={1} size="xl" c="blue.7">
              每日記帳
            </Title>
            <Group gap="md" wrap="nowrap" justify="center">
              <MonthPickerInput
                label="選擇月份"
                placeholder="選擇月份"
                value={showDate.toDate()}
                onChange={(date) => {
                  if (date) {
                    setShowDate(dayjs(date));
                  }
                }}
                size="lg"
                styles={{
                  label: { fontSize: rem(18), fontWeight: 600 },
                  input: { fontSize: rem(16), height: rem(50) }
                }}
              />
              <Button
                color="orange"
                size="lg"
                leftSection={<IconFileText size={20} />}
                onClick={openBatch}
                style={{
                  fontSize: rem(16),
                  height: rem(50),
                  marginTop: rem(25)
                }}
              >
                批量輸入
              </Button>
            </Group>
          </Stack>
        </Paper>


        {/* 每日卡片 */}
        <Grid gutter={{ base: 'sm', sm: 'md' }}>
          {dailyData.map((dayData) => (
            <Grid.Col key={dayData.day} span={{ base: 12, xs: 6, sm: 4, md: 3 }}>
              <Card
                shadow="md"
                padding={{ base: 'sm', sm: 'lg' }}
                radius="md"
                style={{
                  minHeight: rem(160),
                  borderLeft: dayData.income > 0 || dayData.specialIncome > 0 || dayData.expenses > 0 
                    ? `${rem(4)} solid #51cf66` 
                    : `${rem(4)} solid #e9ecef`
                }}
              >
                <Stack gap="sm">
                  {/* 日期頭部 */}
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Box>
                      <Text size={{ base: 'lg', sm: 'xl' }} fw={700} c="dark.7">
                        {dayData.day}日
                      </Text>
                      <Text size={{ base: 'xs', sm: 'sm' }} c="dimmed">
                        星期{dayData.weekName}
                      </Text>
                      {dayData.lunar && (
                        <Badge color="orange" size="sm" style={{ fontSize: rem(12) }}>
                          {dayData.lunar}
                        </Badge>
                      )}
                    </Box>
                    <Group gap="xs" wrap="nowrap">
                      <ActionIcon
                        color="blue"
                        variant="light"
                        size={{ base: 'md', sm: 'lg' }}
                        onClick={() => openEditModal(dayData)}
                      >
                        {dayData.id ? <IconEdit size={16} /> : <IconPlus size={16} />}
                      </ActionIcon>
                      {dayData.id && (
                        <ActionIcon
                          color="red"
                          variant="light"
                          size={{ base: 'md', sm: 'lg' }}
                          onClick={() => deleteRecord(dayData.id)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Group>

                  {/* 金額顯示 */}
                  <Stack gap="xs">
                    {dayData.income > 0 && (
                      <Group gap="xs">
                        <IconCoin size={16} color="#51cf66" />
                        <Text size="sm" c="green.6" fw={500}>
                          收入: ${dayData.income}
                        </Text>
                      </Group>
                    )}
                    {dayData.specialIncomeFrom && (
                      <Text size="xs" c="green.7" fw={500}>
                        {dayData.specialIncomeFrom}
                      </Text>
                    )}
                    {dayData.specialIncome > 0 && (
                      <Group gap="xs">
                        <IconCoin size={16} color="#51cf66" />
                        <Text size="sm" c="green.6" fw={500}>
                          特別收入: ${dayData.specialIncome}
                        </Text>
                      </Group>
                    )}
                    {dayData.vendor && (
                      <Text size="xs" c="blue.7" fw={500}>
                        {dayData.vendor}
                      </Text>
                    )}
                    {dayData.expenses > 0 && (
                      <Group gap="xs">
                        <IconShoppingCart size={16} color="#fa5252" />
                        <Text size="sm" c="red.6" fw={500}>
                          支出: ${dayData.expenses}
                        </Text>
                      </Group>
                    )}
                  </Stack>
                </Stack>
              </Card>
            </Grid.Col>
          ))}
        </Grid>

        {/* 月份總結区塊 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }} bg="gray.0">
          <Title order={2} size="lg" mb="md" c="blue.7">
            {showDate.format('YYYY年MM月')}總結
          </Title>
          <Grid>
            {(() => {
              let totalIncome = 0;
              let totalSpecialIncome = 0;
              let totalExpenses = 0;
              
              dailyData.forEach(day => {
                totalIncome += day.income || 0;
                totalSpecialIncome += day.specialIncome || 0;
                totalExpenses += day.expenses || 0;
              });
              
              const netIncome = totalIncome + totalSpecialIncome - totalExpenses;
              
              return (
                <>
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                    <Card padding="lg" radius="md" withBorder>
                      <Stack align="center" gap="sm">
                        <IconCoin size={32} color="#51cf66" />
                        <Text size="sm" c="dimmed" fw={500} ta="center">一般收入</Text>
                        <Text size="xl" fw={700} c="green.6">
                          ${format_with_substring(totalIncome)}
                        </Text>
                      </Stack>
                    </Card>
                  </Grid.Col>
                  
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                    <Card padding="lg" radius="md" withBorder>
                      <Stack align="center" gap="sm">
                        <IconCoin size={32} color="#51cf66" />
                        <Text size="sm" c="dimmed" fw={500} ta="center">特殊收入</Text>
                        <Text size="xl" fw={700} c="green.6">
                          ${format_with_substring(totalSpecialIncome)}
                        </Text>
                      </Stack>
                    </Card>
                  </Grid.Col>
                  
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                    <Card padding="lg" radius="md" withBorder>
                      <Stack align="center" gap="sm">
                        <IconShoppingCart size={32} color="#fa5252" />
                        <Text size="sm" c="dimmed" fw={500} ta="center">總支出</Text>
                        <Text size="xl" fw={700} c="red.6">
                          ${format_with_substring(totalExpenses)}
                        </Text>
                      </Stack>
                    </Card>
                  </Grid.Col>
                  
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                    <Card padding="lg" radius="md" withBorder style={{
                      borderColor: netIncome >= 0 ? '#51cf66' : '#fa5252',
                      borderWidth: '2px'
                    }}>
                      <Stack align="center" gap="sm">
                        <IconCoin size={32} color={netIncome >= 0 ? '#51cf66' : '#fa5252'} />
                        <Text size="sm" c="dimmed" fw={500} ta="center">淨結餘</Text>
                        <Text size="xl" fw={700} c={netIncome >= 0 ? "green.6" : "red.6"}>
                          ${format_with_substring(netIncome)}
                        </Text>
                      </Stack>
                    </Card>
                  </Grid.Col>
                </>
              );
            })()}
          </Grid>
        </Paper>

        {/* 特殊節日比較表格 */}
        {festivalData.length > 0 && (
          <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }}>
            <Title order={2} size="lg" mb="md" c="blue.7">
              特殊節日收入比較
            </Title>
            <ScrollArea>
              <Table fontSize="sm" withRowBorders withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ textAlign: 'center', minWidth: rem(80) }}>節日</Table.Th>
                    {Array.from({ length: dayjs().year() - 2009 }, (_, i) => {
                      const yearNum = dayjs().year() - i;
                      return (
                        <Table.Th key={yearNum} style={{ textAlign: 'center', minWidth: rem(70) }}>
                          {yearNum}
                        </Table.Th>
                      );
                    })}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {festivalData.map((row, index) => (
                    row.festival && (
                      <Table.Tr key={index}>
                        <Table.Td style={{ textAlign: 'center', fontWeight: 600 }}>
                          {row.festival}
                        </Table.Td>
                        {Array.from({ length: dayjs().year() - 2009 }, (_, i) => {
                          const yearNum = dayjs().year() - i;
                          return (
                            <Table.Td key={yearNum} style={{ textAlign: 'right' }}>
                              {row[yearNum] ? format_with_substring(row[yearNum]) : ''}
                            </Table.Td>
                          );
                        })}
                      </Table.Tr>
                    )
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
        )}

      </Stack>

      {/* 編輯模態框 */}
      <Modal
        opened={opened}
        onClose={close}
        title={
          <Title order={3} size="lg">
            {editRecord?.id ? '編輯記錄' : '新增記錄'} - {editRecord?.day}日
          </Title>
        }
        size="lg"
        centered
      >
        {editRecord && (
          <EditForm
            record={editRecord}
            onSave={saveRecord}
            onSaveAndNext={saveRecordAndNext}
            onCancel={close}
            isLastDay={editRecord.day >= showDate.endOf('month').date()}
          />
        )}
      </Modal>

      {/* 批量輸入模態框 */}
      <Modal
        opened={batchOpened}
        onClose={closeBatch}
        title={
          <Group gap="sm" align="center">
            <IconFileText size={24} color="var(--mantine-color-orange-7)" />
            <Stack gap={0}>
              <Title order={2} size="xl" c="orange.7" visibleFrom="sm">
                批量輸入 - {showDate.format('YYYY年MM月')}每日收入
              </Title>
              <Text size="sm" c="orange.6" hiddenFrom="sm">
                {showDate.format('YYYY/MM')} 每日收入
              </Text>
            </Stack>
          </Group>
        }
        size="xl"
        centered
        styles={{
          content: { maxHeight: '90vh', overflow: 'auto' }
        }}
      >
        <BatchInputSection 
          showDate={showDate}
          dailyData={dailyData}
          onSave={saveBatchRecords}
          onClose={closeBatch}
        />
      </Modal>
    </Container>
  );
};

// 編輯表單組件
const EditForm = ({ record, onSave, onSaveAndNext, onCancel, isLastDay }) => {
  const [formData, setFormData] = useState(record);
  const [specialIncomeFromOptions, setSpecialIncomeFromOptions] = useState([
    '親家',
    '阿賢',
    '亞田份',
    '金紙包',
    '姊',
    '邱太',
    '秀敏',
    '同安金紙',
    '鳳凰金紙',
    '春日金紙',
    '大業金紙',
    '南平金紙',
    '聖賢宮',
    '聖母宮'
  ]);

  // 監聽 record 的變化，更新 formData
  useEffect(() => {
    setFormData(record);
  }, [record]);

  const handleSave = () => {
    onSave(formData);
  };

  const handleSaveAndNext = () => {
    onSaveAndNext(formData);
  };

  const expenseCategories = [
    '小神堂',
    '源吉',
    '祥雲',
    '揚明',
    '伯明',
    '吉興',
    '祐恆',
    '香',
    '包-花袋',
    '雜支',
    '營業稅',
    '打火機'
  ];

  return (
    <Stack gap={rem(12)}>
      <NumberInput
        label="收入金額"
        placeholder="0"
        value={formData.income || 0}
        onChange={(value) => setFormData({...formData, income: value || 0})}
        min={0}
        size="lg"
        styles={{
          label: { fontSize: rem(16), fontWeight: 600 },
          input: { fontSize: rem(16), height: rem(50) }
        }}
      />

      <SpecialIncomeCombobox 
        label="特別項目"
        value={formData.specialIncomeFrom || ''}
        onChange={(value) => setFormData({...formData, specialIncomeFrom: value})}
        options={specialIncomeFromOptions}
        onAddOption={(newOption) => {
          if (newOption && !specialIncomeFromOptions.includes(newOption)) {
            setSpecialIncomeFromOptions(prev => [...prev, newOption]);
          }
        }}
      />

      <NumberInput
        label="特別收入"
        placeholder="0"
        value={formData.specialIncome || 0}
        onChange={(value) => setFormData({...formData, specialIncome: value || 0})}
        min={0}
        size="lg"
        styles={{
          label: { fontSize: rem(16), fontWeight: 600 },
          input: { fontSize: rem(16), height: rem(50) }
        }}
      />

      <Select
        key={`vendor-${formData.day}-${formData.id || 'new'}`}
        label="收款廠商"
        placeholder="選擇廠商"
        data={expenseCategories}
        value={formData.vendor || null}
        onChange={(value) => setFormData({...formData, vendor: value || ''})}
        clearable
        size="lg"
        styles={{
          label: { fontSize: rem(16), fontWeight: 600 },
          input: { fontSize: rem(16), height: rem(50) }
        }}
      />

      <NumberInput
        label="支出金額"
        placeholder="0"
        value={formData.expenses || 0}
        onChange={(value) => setFormData({...formData, expenses: value || 0})}
        min={0}
        size="lg"
        styles={{
          label: { fontSize: rem(16), fontWeight: 600 },
          input: { fontSize: rem(16), height: rem(50) }
        }}
      />

      <Group justify="center" mt="lg">
        <Button
          color="green"
          size="lg"
          leftSection={<IconCheck size={20} />}
          onClick={handleSave}
          style={{
            fontSize: rem(16),
            height: rem(50),
            minWidth: rem(120)
          }}
        >
          儲存
        </Button>
        {!isLastDay && (
          <Button
            color="blue"
            size="lg"
            leftSection={<IconCheck size={20} />}
            onClick={handleSaveAndNext}
            style={{
              fontSize: rem(16),
              height: rem(50),
              minWidth: rem(140)
            }}
          >
            儲存並繼續
          </Button>
        )}
        <Button
          variant="outline"
          color="gray"
          size="lg"
          leftSection={<IconX size={20} />}
          onClick={onCancel}
          style={{
            fontSize: rem(16),
            height: rem(50),
            minWidth: rem(120)
          }}
        >
          取消
        </Button>
      </Group>
    </Stack>
  );
};

// 批量輸入組件
const BatchInputSection = ({ showDate, dailyData, onSave, onClose }) => {
  const [batchText, setBatchText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // 生成批量輸入文字
  const generateBatchText = () => {
    const maxDay = showDate.endOf('month').date();
    const lines = [];
    
    for (let day = 1; day <= maxDay; day++) {
      const dayRecord = dailyData.find(d => d.day === day);
      const income = dayRecord?.income || 0;
      const specialIncome = dayRecord?.specialIncome || 0;
      const expenses = dayRecord?.expenses || 0;

      const hasData = income > 0 || specialIncome > 0 || expenses > 0;
      
      // 使用逗號分隔的格式：收入,特殊收入,支出
      lines.push(hasData ? `${income},${specialIncome},${expenses}` : '');
    }

    let lastNonEmptyIndex = lines.length - 1;
    while (lastNonEmptyIndex >= 0 && lines[lastNonEmptyIndex].trim() === '') {
      lastNonEmptyIndex -= 1;
    }

    const trimmedLines = lastNonEmptyIndex >= 0 ? lines.slice(0, lastNonEmptyIndex + 1) : [];
    setBatchText(trimmedLines.join('\n'));
  };

  // 當月份改變時更新文字框內容
  useEffect(() => {
    if (!isEditing) {
      generateBatchText();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDate, dailyData, isEditing]);

  // 初始化數據
  useEffect(() => {
    generateBatchText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 解析批量文字為記錄陣列
  const parseBatchText = () => {
    const lines = batchText.split('\n');
    const maxDay = showDate.endOf('month').date();
    const records = [];

    for (let i = 0; i < Math.min(lines.length, maxDay); i++) {
      const day = i + 1;
      const line = lines[i].trim();
      
      if (!line) continue; // 跳過空行
      
      const existingRecord = dailyData.find(d => d.day === day);
      let income = 0, specialIncome = 0, expenses = 0;
      
      // 檢查是否使用逗號分隔的格式
      if (line.includes(',')) {
        const parts = line.split(',');
        // 支援靈活的逗號分隔：可以有1-3個部分
        income = parts[0] && parts[0].trim() !== '' ? parseFloat(parts[0].trim()) || 0 : (existingRecord?.income || 0);
        specialIncome = parts[1] && parts[1].trim() !== '' ? parseFloat(parts[1].trim()) || 0 : (existingRecord?.specialIncome || 0);
        expenses = parts[2] && parts[2].trim() !== '' ? parseFloat(parts[2].trim()) || 0 : (existingRecord?.expenses || 0);
      } else {
        // 向後兼容：單純數字表示收入
        income = parseFloat(line) || 0;
        specialIncome = existingRecord?.specialIncome || 0;
        expenses = existingRecord?.expenses || 0;
      }
      
      // 只要有任一項目有值就創建記錄
      if (income > 0 || specialIncome > 0 || expenses > 0) {
        records.push({
          id: existingRecord?.id,
          day: day,
          income: income,
          specialIncome: specialIncome,
          specialIncomeFrom: existingRecord?.specialIncomeFrom || '',
          vendor: existingRecord?.vendor || '',
          expenses: expenses,
          lunar: existingRecord?.lunar || '',
          memo: existingRecord?.memo || ''
        });
      }
    }

    return records;
  };

  // 批量儲存
  const handleBatchSave = async () => {
    const records = parseBatchText();
    
    if (records.length === 0) {
      notifications.show({
        title: '無效資料',
        message: '請輸入至少一筆有效數字',
        color: 'orange',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(records);
      setIsEditing(false);
      if (onClose) {
        onClose(); // 儲存成功後關閉模態框
      }
    } catch (error) {
      console.error('Batch save error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 取消編輯
  const handleCancel = () => {
    setIsEditing(false);
    generateBatchText();
  };

  const maxDay = showDate.endOf('month').date();
  
  // 生成帶行號的文字內容
  const generateTextareaWithLineNumbers = () => {
    if (!batchText) return '';
    const lines = batchText.split('\n');
    return lines.map((line, index) => `${String(index + 1).padStart(2, ' ')}: ${line}`).join('\n');
  };


  return (
    <Stack gap="md">
          <Stack gap="md">
            <Text size="sm" c="dimmed" visibleFrom="sm">
              每一行代表一天的資料，從1日到{maxDay}日。支援多種格式：<br/>
              • 單一數字：僅收入金額（如：5000）<br/>
              • 逗號分隔：收入,特殊收入,支出（如：5000,1200,3000）<br/>
              • 彈性省略：可省略任何欄位（如：5000,, | ,1200, | 5000,1200）<br/>
              空行或全為空值表示當天無資料。
            </Text>
            <Text size="sm" c="dimmed" hiddenFrom="sm">
              每行依序代表一天，可輸入「收入,特別,支出」，空行代表無資料。
            </Text>

            <Grid>
              <Grid.Col span={{ base: 12, lg: 8 }}>
                <Stack gap="sm">
                  {!isEditing && (
                    <Group justify="space-between">
                      <Text size="sm" fw={500} c="gray.7">
                        當前數據預覽（共 {maxDay} 天）
                      </Text>
                      <Button
                        color="orange"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(true)}
                        leftSection={<IconEdit size={14} />}
                      >
                        開始編輯
                      </Button>
                    </Group>
                  )}
                  
                  <Box style={{ position: 'relative' }}>
                    <Textarea
                      value={isEditing ? generateTextareaWithLineNumbers() : generateTextareaWithLineNumbers()}
                      onChange={(event) => {
                        if (isEditing) {
                          // 從帶行號的文字中提取純數值
                          const lines = event.target.value.split('\n');
                          const pureValues = lines.map(line => {
                            const match = line.match(/^\s*\d+:\s*(.*)$/);
                            return match ? match[1] : line;
                          });
                          setBatchText(pureValues.join('\n'));
                        }
                      }}
                      onKeyDown={(event) => {
                        if (isEditing && event.key === 'Enter') {
                          // 阻止默認行為，讓 Enter 鍵正常換行
                          event.stopPropagation();
                        }
                      }}
                      placeholder={`請輸入${maxDay}天的資料，每行一筆...\n例如：\n01: 5000,1200,3000\n02: 4200,,1500\n03: ,2000,\n04: 7400\n...`}
                      minRows={Math.min(maxDay, 20)}
                      maxRows={Math.min(maxDay, 25)}
                      autosize
                      styles={{
                        input: {
                          fontSize: rem(13),
                          lineHeight: '1.6',
                          fontFamily: 'Consolas, "Courier New", monospace',
                          backgroundColor: isEditing ? 'white' : '#f8f9fa',
                          border: isEditing ? '2px solid var(--mantine-color-orange-4)' : '1px solid #dee2e6',
                          padding: rem(12)
                        }
                      }}
                      readOnly={!isEditing}
                    />
                    
                    {/* 行數指示器 */}
                    {isEditing && (
                      <Box style={{
                        position: 'absolute',
                        top: rem(8),
                        right: rem(8),
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        padding: `${rem(4)} ${rem(8)}`,
                        borderRadius: rem(4),
                        fontSize: rem(11),
                        color: 'var(--mantine-color-gray-6)',
                        pointerEvents: 'none'
                      }}>
                        共 {batchText.split('\n').length} 行 / {maxDay} 天
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, lg: 4 }}>
                <Paper withBorder p="md" style={{ height: 'fit-content', maxHeight: rem(400), overflow: 'auto' }}>
                  <Stack gap="sm">
                    <Text size="sm" fw={600} c="gray.7">
                      資料預覽 {isEditing && `(前15天)`}
                    </Text>
                    {(isEditing ? batchText.split('\n') : batchText.split('\n'))
                      .slice(0, 15)
                      .map((value, index) => {
                        const line = value.trim();
                        let income = 0, specialIncome = 0, expenses = 0;
                        
                        if (line.includes(',')) {
                          const parts = line.split(',');
                          // 支援靈活的逗號分隔格式
                          income = parts[0] && parts[0].trim() !== '' ? parseFloat(parts[0].trim()) || 0 : 0;
                          specialIncome = parts[1] && parts[1].trim() !== '' ? parseFloat(parts[1].trim()) || 0 : 0;
                          expenses = parts[2] && parts[2].trim() !== '' ? parseFloat(parts[2].trim()) || 0 : 0;
                        } else {
                          income = parseFloat(line) || 0;
                        }
                        
                        const hasData = income > 0 || specialIncome > 0 || expenses > 0;
                        
                        return (
                          <Stack key={index} gap="xs">
                            <Group justify="space-between" gap="xs">
                              <Text 
                                size="xs" 
                                c="dimmed"
                                style={{ fontFamily: 'monospace', minWidth: rem(35) }}
                              >
                                {String(index + 1).padStart(2, '0')}日:
                              </Text>
                              <Text 
                                size="xs" 
                                c={hasData ? "blue.6" : "gray.5"}
                                fw={hasData ? 600 : 400}
                                style={{ fontFamily: 'monospace', fontSize: rem(10) }}
                              >
                                {hasData ? `收:${income} 特:${specialIncome} 支:${expenses}` : '無資料'}
                              </Text>
                            </Group>
                          </Stack>
                        );
                      })}
                    {batchText.split('\n').length > 15 && (
                      <Text size="xs" c="dimmed" ta="center">
                        ... 及其他 {batchText.split('\n').length - 15} 天
                      </Text>
                    )}
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>

            {isEditing && (
              <Group justify="center" mt="md">
                <Button
                  color="green"
                  size="lg"
                  leftSection={<IconCheck size={20} />}
                  onClick={handleBatchSave}
                  loading={isSubmitting}
                  style={{
                    fontSize: rem(16),
                    height: rem(48),
                    minWidth: rem(120)
                  }}
                >
                  批量儲存
                </Button>
                <Button
                  variant="outline"
                  color="gray"
                  size="lg"
                  leftSection={<IconX size={20} />}
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  style={{
                    fontSize: rem(16),
                    height: rem(48),
                    minWidth: rem(120)
                  }}
                >
                  取消
                </Button>
              </Group>
            )}
          </Stack>
    </Stack>
  );
};

// 特別項目 Combobox 組件
const SpecialIncomeCombobox = ({ label, value, onChange, options, onAddOption }) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [search, setSearch] = useState(value);
  const [data, setData] = useState(options);

  useEffect(() => {
    setData(options);
  }, [options]);

  useEffect(() => {
    setSearch(value);
  }, [value]);

  const exactOptionMatch = data.some((item) => item === search);
  const filteredOptions = exactOptionMatch
    ? data
    : data.filter((item) => item.toLowerCase().includes(search.toLowerCase().trim()));

  const comboboxOptions = filteredOptions.map((item) => (
    <Combobox.Option value={item} key={item}>
      {item}
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(val) => {
        if (val === '$create') {
          const newOption = search.trim();
          if (newOption && !data.includes(newOption)) {
            setData((current) => [...current, newOption]);
            onAddOption(newOption);
          }
          onChange(newOption);
          setSearch(newOption);
        } else {
          onChange(val);
          setSearch(val);
        }
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          label={label}
          rightSection={<Combobox.Chevron />}
          value={search}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
            onChange(search);
          }}
          placeholder="選擇或輸入特別項目"
          rightSectionPointerEvents="none"
          size="lg"
          styles={{
            label: { fontSize: rem(16), fontWeight: 600 },
            input: { fontSize: rem(16), height: rem(50) }
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {comboboxOptions}
          {!exactOptionMatch && search.trim().length > 0 && (
            <Combobox.Option value="$create">
              + 新增「{search.trim()}」
            </Combobox.Option>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default ElderlyKeyinIndex;
